//* seller용 배송상태 관리 카드
import { DragEvent, useRef, useState } from 'react';

import { useSetRecoilState } from 'recoil';

import { useDeliveredImageUpload } from '@service/seller/hooks/useDeliveredImageUpload';
import { ShopOrderForSeller } from '@shared/api/shopOrder/shopOrder.interface';
import { orderContentString as shopOrderForSellerContentString } from '@shared/api/shopOrder/shopOrder.utils';
import { Button } from '@shared/components/Button';
import { Icon } from '@shared/components/icons';
import { DeliveredImageUploader } from '@shared/components/ImageUploader';
import { DeliveryProgressIndicator } from '@shared/containers/DeliveryProgressIndicator/DeliveryProgressIndicator';
import { DeathCharacter } from '@shared/miscValues';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { DeliveryState, PaymentState } from '@shared/types';
import {
  fileBucketUrl,
  readFileAsDataUrl,
  validateImageFile,
} from '@shared/utils/fileUtils';
import { formatDate } from '@shared/utils/formatDate';
import { Image, Tag } from 'antd';

import { DeliveryContentCard } from './DeliveryContentCard';
import { DeliveryStatusManageButton } from './DeliveryStatusManageButton';
import { OrderBugoDetailEditModal } from './OrderBugoDetailEditModal';
import { ShopOrderDetailInfoCard } from './ShopOrderDetailInfoCard';

interface DeliveryManageCardProps {
  order: ShopOrderForSeller;
}

export const DeliveryManageCard = function DeliveryManageCard({
  order,
}: DeliveryManageCardProps) {
  const [detailOpen, setDetailOpen] = useState(false);
  const [contentOpen, setContentOpen] = useState(false);
  const deliveryDetail = order.deliveryDetail;
  const shopItem = order.orderDetail.shopItem;
  const openId = `deliveryManageCard-${order._id}`;
  const setDeliveredImageUpload = useSetRecoilState(simpleOpenAtomFamily(openId));
  const newTag = (order: ShopOrderForSeller) => {
    if (
      order.deliveryDetail.status === DeliveryState.BEFORE_PREPARING &&
      [PaymentState.PAID, PaymentState.CONFIRMED].includes(order.paymentDetail.status)
    ) {
      return <Tag className="filled-warn border-0 py-1 font-bold opacity-80">NEW</Tag>;
    }
  };

  const agencyTag = (order: ShopOrderForSeller) => {
    return (
      <Tag className="filled-gray-800 border-0 py-1 font-bold opacity-80">
        {order.bugo?.bugoBrand?.bugoAgency?.nickName}
      </Tag>
    );
  };

  const editOpenId = `deliveryManageCard-edit-phrase-${order._id}`;
  const setEditOpen = useSetRecoilState(simpleOpenAtomFamily(editOpenId));

  const { UpFromBracket, Copy } = Icon();

  //* 배송완료사진 퀵 드롭 업로드
  const { upload, isUploading } = useDeliveredImageUpload(order);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingPreview, setPendingPreview] = useState<string>();
  const [isDragging, setIsDragging] = useState(false);
  const [zoomVisible, setZoomVisible] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    setIsDragging(true);
  };
  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragging(false);
    }
  };
  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file || !validateImageFile(file)) return;
    setPendingFile(file);
    setPendingPreview(await readFileAsDataUrl(file));
  };

  const handleQuickSave = async () => {
    if (!pendingFile) return;
    const ok = await upload(pendingFile);
    if (ok) {
      setPendingFile(null);
      setPendingPreview(undefined);
    }
  };
  const handleQuickCancel = () => {
    setPendingFile(null);
    setPendingPreview(undefined);
  };

  const priceWhole = shopItem.shopItemInfo.priceWhole;
  return (
    <>
      <DeliveredImageUploader order={order} openId={openId} />
      <div
        key={order._id}
        className="relative min-w-[320px] font-sans"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDragging && (
          <div className="center-box pointer-events-none absolute inset-0 z-20 rounded-lg border-2 border-dashed border-cyan-500 bg-cyan-50/80">
            <div className="center-box gap-2 text-cyan-700">
              <UpFromBracket className="wh-6 fill-cyan-700" />
              <p className="text-base font-bold">여기에 놓으면 배송완료사진 업로드</p>
            </div>
          </div>
        )}
        <div className="absolute top-0 right-0">
          {agencyTag(order)} {newTag(order)}
        </div>
        <div className="h-auto w-full overflow-hidden rounded-lg border shadow">
          <div className="p-3 pb-2">
            <div className="flex items-center">
              <div className="w-full">
                <p className="flex-wrap truncate text-base font-medium text-gray-900">
                  {shopItem.name ?? shopItem.shopItemInfo.name}
                </p>
                <div className="flex items-center justify-between">
                  <p className="flex-wrap truncate text-base font-medium text-gray-900">
                    {priceWhole.toLocaleString()}원{' '}
                    {order.paymentDetail.provider === 'Tosspayments' ? 'T ' : 'B '}
                    {order.paymentDetail.method
                      ? order.paymentDetail.method === '가상계좌'
                        ? '가상#'
                        : order.paymentDetail.method === '계좌 입금'
                        ? '입금#'
                        : order.paymentDetail.method.slice(0, 2)
                      : ''}
                    <span className="text-gray-500" title={`주문번호: ${order._id}`}>
                      {' ' + order._id.slice(-6)}
                    </span>
                  </p>
                  <p className="text-base font-medium">
                    {DeathCharacter}
                    {order.bugo?.fevent?.deceasedInfo?.name}님
                  </p>
                </div>
                <div className="mt-1">
                  <p className="whitespace-pre-wrap break-keep text-sm text-gray-700">
                    {deliveryDetail.receiverAddress}
                  </p>
                  <div className="mt-1 flex items-center justify-between">
                    <div className="w-3/4">
                      <p className="truncate text-sm font-bold text-gray-700">
                        보내는분: {order.bugoDetail.senderPhrase}
                      </p>
                      <p className="truncate text-sm font-bold text-gray-700">
                        조의문구: {order.bugoDetail.condolencePhrase}
                      </p>
                    </div>
                    <div>
                      <Button
                        className="shadow-sm"
                        onClick={() => {
                          setEditOpen(true);
                        }}
                      >
                        문구변경
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-1 whitespace-pre">
                    <p className="text-sm text-gray-700">
                      결제:{' '}
                      {formatDate(order.paymentDetail.purchasedAt, {
                        dateSeparater: '.',
                        contains: {
                          year: false,
                          day: true,
                          hours: true,
                          minutes: true,
                        },
                      })}
                    </p>
                    {order.deliveryDetail?.estimatedDeliveredAt && (
                      <p className="text-sm text-gray-700">
                        예상:{' '}
                        {formatDate(order.deliveryDetail.estimatedDeliveredAt, {
                          dateSeparater: '.',
                          contains: {
                            year: false,
                            day: true,
                            hours: true,
                            minutes: true,
                          },
                        })}
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-1 whitespace-pre">
                    <p className="text-sm text-gray-700">
                      입실:{' '}
                      {formatDate(order.bugo?.fevent?.deceasedInfo?.enterance?.date, {
                        dateSeparater: '.',
                        contains: {
                          year: false,
                          day: true,
                          hours: true,
                          minutes: true,
                        },
                      })}
                    </p>
                    <p className="text-sm text-gray-700">
                      발인:{' '}
                      {formatDate(order.bugo?.fevent?.deceasedInfo?.coffinOut?.date, {
                        dateSeparater: '.',
                        contains: {
                          year: false,
                          day: true,
                          hours: true,
                          minutes: true,
                        },
                      })}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-1 whitespace-pre">
                    {order.paymentDetail?.canceledAt && (
                      <p className="text-sm text-gray-700">
                        취소:{' '}
                        {formatDate(order.paymentDetail.canceledAt, {
                          dateSeparater: '.',
                          contains: {
                            year: false,
                            day: true,
                            hours: true,
                            minutes: true,
                          },
                        })}
                      </p>
                    )}
                    {order.paymentDetail?.cancelReason && (
                      <p className="text-sm text-gray-700">
                        사유: {order.paymentDetail?.cancelReason}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <DeliveryProgressIndicator order={order} size="small" />
            <div className="flex space-x-3 pt-3">
              <DeliveryStatusManageButton shopOrder={order} />
            </div>
            <div className="flex justify-between pt-3">
              {order.deliveryDetail.deliveredImage ? (
                <div className="w-1/2">
                  <Image
                    src={fileBucketUrl(order.deliveryDetail.deliveredImage?.url, 'w200')}
                    alt="배송 완료사진"
                    preview={{
                      src: fileBucketUrl(order.deliveryDetail.deliveredImage?.url, 'raw'),
                    }}
                  />
                </div>
              ) : (
                <div></div>
              )}
              <div
                className="flex h-fit cursor-pointer gap-1 rounded-lg border border-gray-400 p-1.5"
                onClick={() => {
                  setDeliveredImageUpload(true);
                }}
              >
                <p className="font-medium">배송 완료 사진</p>
                <UpFromBracket className="wh-5 fill-gray-700" />
              </div>
            </div>
            {pendingFile && (
              <div className="mt-3 w-full rounded-lg border border-cyan-200 bg-cyan-50/40 p-3">
                <p className="mb-2 text-sm font-medium text-gray-700">
                  업로드할 배송 완료 사진
                </p>
                <div className="w-full overflow-hidden rounded-md">
                  <Image
                    src={pendingPreview}
                    alt="배송완료 미리보기"
                    className="max-h-80 w-full object-contain"
                    preview={{
                      visible: zoomVisible,
                      onVisibleChange: (v) => setZoomVisible(v),
                      src: pendingPreview,
                    }}
                  />
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  <Button
                    type="button"
                    className="border border-gray-300"
                    disabled={isUploading}
                    onClick={() => setZoomVisible(true)}
                  >
                    확대보기
                  </Button>
                  <Button
                    type="button"
                    className="border border-gray-300"
                    disabled={isUploading}
                    onClick={handleQuickCancel}
                  >
                    취소
                  </Button>
                  <Button
                    type="button"
                    className="filled-gray-800"
                    disabled={isUploading}
                    onClick={handleQuickSave}
                  >
                    {isUploading ? '저장 중...' : '저장'}
                  </Button>
                </div>
              </div>
            )}
          </div>
          <div className="space-y-4 bg-gray-50 px-5 py-3">
            <div className="flex items-center justify-between text-sm">
              <button
                className="font-medium text-cyan-700 hover:text-cyan-900"
                onClick={() => {
                  setDetailOpen(!detailOpen);
                }}
              >
                View all
              </button>
              <button
                className="font-medium text-cyan-700 hover:text-cyan-900"
                onClick={() => {
                  setContentOpen(!contentOpen);
                }}
              >
                발주용 내용 보기
              </button>
            </div>
            {detailOpen && (
              <div>
                <div className="py-2">
                  <p className="-mt-4 font-medium">주문번호: {order._id}</p>
                </div>
                <ShopOrderDetailInfoCard shopOrder={order} />
              </div>
            )}
            {contentOpen && (
              <div>
                <div className="flex justify-between py-2 px-2 font-medium">
                  <p>발주내용</p>
                  <div className="px-1">
                    <Button
                      className="center-box button-icon gap-0.5"
                      onClick={async (): Promise<void> => {
                        navigator.clipboard.writeText(
                          shopOrderForSellerContentString(order),
                        );
                        alert('복사 완료');
                      }}
                    >
                      <Copy className="wh-4" />
                      <p className="keep-all-preline text-sm">복사</p>
                    </Button>
                  </div>
                </div>
                <DeliveryContentCard shopOrder={order} />
              </div>
            )}
          </div>
        </div>
      </div>
      <OrderBugoDetailEditModal openId={editOpenId} order={order} />
    </>
  );
};
